import { Link } from "react-router-dom";
import { cn } from "@volca/ui/lib/utils";
import { useReportCurrency } from "@/profitability/currency";
import {
  ObjectTable,
  type TableColumn,
} from "@/profitability/records/object-table";
import { isProfit, useReviewSpec } from "@/profitability/review/review-spec";
import { profitabilityRulePath } from "@/profitability/routes";
import { money } from "./format";
import type { TrendPeriod, TrendRule } from "./types";

const share = (part: number, whole: number) =>
  whole ? `${Math.round((part / whole) * 100)}%` : "—";

/**
 * How many records a rule caught each period, as small bars, oldest first.
 * Insights' rules table and Settings → Review rules both show it.
 */
export const RuleCounts = ({
  counts: byRun,
  periods,
  evaluation = {},
}: {
  evaluation?: Record<string, string>;
  counts: Record<string, number>;
  periods: Pick<TrendPeriod, "run_id">[];
}) => {
  const counts = periods.map((period) => byRun[period.run_id] ?? 0);
  const labels = periods.map((period, index) => {
    const state =
      evaluation[period.run_id] ??
      (byRun[period.run_id] === undefined ? "not_evaluated" : "evaluated");
    return state === "disabled"
      ? "off"
      : state === "not_evaluated"
        ? "not evaluated"
        : `${counts[index]}${state === "partial" ? " (partially evaluated)" : ""}`;
  });
  const top = Math.max(...counts, 1);
  const last = periods.length - 1;
  return (
    <span
      className="inline-flex h-5 items-end gap-0.5"
      role="img"
      aria-label={`By period: ${labels.join(", ")}`}
    >
      {periods.map((period, index) => {
        const value = counts[index] ?? 0;
        return (
          <span
            key={period.run_id}
            title={labels[index]}
            className={cn("w-1.5 rounded-xs", {
              "bg-ink":
                index === last &&
                labels[index] !== "off" &&
                labels[index] !== "not evaluated",
              "bg-subtlest":
                index !== last &&
                labels[index] !== "off" &&
                labels[index] !== "not evaluated",
              "border border-line":
                labels[index] === "off" || labels[index] === "not evaluated",
            })}
            style={{
              height: `${Math.max((value / top) * 100, value ? 15 : 4)}%`,
            }}
          />
        );
      })}
    </span>
  );
};

/**
 * Each flag rule as reviewers judged it: what it caught period by period,
 * what those records fell below the average margin by (only when the
 * report's rate is a profit margin), and how often reviewers called it
 * noise. The rules to tune are the noisy ones.
 */
export const RulesTable = ({
  reportId,
  runId,
  rules,
  periods,
  noun,
  flagged,
}: {
  reportId: string;
  runId?: string | null;
  rules: TrendRule[];
  periods: TrendPeriod[];
  noun: string;
  /** What the rules flag, plural: "jobs". */
  flagged: string;
}) => {
  const spec = useReviewSpec();
  const currency = useReportCurrency();
  const shortfall: TableColumn<TrendRule>[] = isProfit(spec)
    ? [
        {
          id: "shortfall",
          header: `Under average ${spec.metric.label}`,
          align: "end",
          cell: (rule) => (
            <span className="tabular-nums">
              {money(rule.shortfall, currency)}
            </span>
          ),
          sortValue: (rule) => rule.shortfall,
          total: money(
            rules.reduce((sum, rule) => sum + rule.shortfall, 0),
            currency,
          ),
        },
      ]
    : [];
  return (
    <ObjectTable
      label="Flag rules"
      rows={rules}
      rowKey={(rule) => rule.id ?? rule.rule}
      countLabel={`${rules.length} ${rules.length === 1 ? "rule" : "rules"}`}
      defaultSort={{ column: "findings", direction: "desc" }}
      columns={[
        {
          id: "rule",
          header: "Rule",
          cell: (rule) =>
            rule.id ? (
              <Link
                to={profitabilityRulePath(reportId, rule.id, runId)}
                className="font-medium underline decoration-line underline-offset-4 hover:decoration-ink"
              >
                {rule.rule}
              </Link>
            ) : (
              <span className="font-medium">{rule.rule}</span>
            ),
          sortValue: (rule) => rule.rule,
        },
        {
          id: "trend",
          header: `By ${noun}`,
          cell: (rule) => (
            <RuleCounts
              counts={rule.counts}
              evaluation={rule.evaluation}
              periods={periods}
            />
          ),
        },
        {
          id: "findings",
          header: `${flagged[0]?.toUpperCase()}${flagged.slice(1)} flagged`,
          align: "end",
          cell: (rule) => <span className="tabular-nums">{rule.findings}</span>,
          sortValue: (rule) => rule.findings,
          total: rules.reduce((sum, rule) => sum + rule.findings, 0),
        },
        ...shortfall,
        {
          id: "noise",
          header: "Rated noise",
          align: "end",
          cell: (rule) => (
            <span className="tabular-nums">
              {rule.up + rule.down
                ? `${share(rule.down, rule.up + rule.down)} of ${rule.up + rule.down}`
                : "Not rated"}
            </span>
          ),
          sortValue: (rule) =>
            rule.up + rule.down ? rule.down / (rule.up + rule.down) : null,
        },
        {
          id: "explained",
          header: "Explained",
          align: "end",
          cell: (rule) => (
            <span className="tabular-nums">
              {share(rule.explained, rule.findings)}
            </span>
          ),
          sortValue: (rule) =>
            rule.findings ? rule.explained / rule.findings : null,
        },
      ]}
    />
  );
};
